<template>
  <div>
    <h2 class="text-h5 mb-4">
      Воронка
    </h2>

    <analytics-filters
      v-model="filters"
      :show-attribution="false"
      @apply="load"
    ></analytics-filters>

    <v-alert
      v-if="error"
      type="error"
      dense
      class="mb-4"
    >
      {{ error }}
    </v-alert>

    <v-row v-if="stats" dense class="mb-4">
      <v-col
        v-for="card in summaryCards"
        :key="card.label"
        cols="6"
        sm="4"
        md="2"
      >
        <v-card
          outlined
          class="pa-3 text-center funnel-stage"
          @click="openStage(card.stage)"
        >
          <div class="text-caption grey--text">
            {{ card.label }}
          </div>
          <div class="text-h6 font-weight-bold">
            {{ card.value }}
          </div>
          <div
            v-if="card.hint"
            class="text-caption grey--text"
          >
            {{ card.hint }}
          </div>
          <div
            v-if="card.cr != null"
            class="text-caption"
          >
            CR {{ card.cr }}%
          </div>
        </v-card>
      </v-col>
    </v-row>

    <v-card outlined class="mb-4 pa-4">
      <vue-apex-charts
        v-if="stats"
        type="bar"
        height="320"
        :options="chartOptions"
        :series="chartSeries"
      ></vue-apex-charts>
      <div
        v-else-if="loading"
        class="text-center py-8"
      >
        <v-progress-circular indeterminate></v-progress-circular>
      </div>
    </v-card>

    <v-alert
      v-if="stats"
      type="info"
      text
      dense
    >
      <div>
        Визиты — сессии сайта; открытия — уникальные сессии с формой;
        заявки / записи / пришли / купили — уникальные клиенты.
      </div>
      <div v-if="filters.company_id" class="mt-1">
        Филиал режет заявки, записи, приходы и покупки.
        Визиты и открытия — по всему сайту.
      </div>
      <div class="mt-1">
        Клиентов с известным источником:
        {{ stats.customers_with_source }} из {{ stats.customers_total }}
        (неизвестный: {{ stats.unknown_source_share_pct }}%).
        Выручка: {{ formatMoney(stats.revenue) }} ₽
      </div>
      <div class="mt-1">
        Нажмите на этап, чтобы открыть список идентифицированных клиентов.
      </div>
    </v-alert>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import Api from '@/api/api'
import AnalyticsFilters from './AnalyticsFilters.vue'

function stripEmpty(obj) {
  const out = {}
  Object.keys(obj || {}).forEach(k => {
    const v = obj[k]
    if (v !== '' && v !== null && v !== undefined) out[k] = v
  })
  return out
}

export default {
  name: 'AnalyticsFunnel',
  components: {
    VueApexCharts,
    AnalyticsFilters,
  },
  data() {
    return {
      filters: {
        date_from: '',
        date_to: '',
        attribution: 'lead',
        source: '',
        campaign: '',
        company_id: null,
      },
      stats: null,
      loading: false,
      error: null,
    }
  },
  computed: {
    summaryCards() {
      if (!this.stats) return []
      const c = this.stats.conversions || {}
      return [
        { label: 'Визиты', value: this.stats.visits, cr: null, stage: 'visits', hint: 'сессии' },
        {
          label: 'Открыли форму',
          value: this.stats.form_opens,
          cr: c.visit_to_open,
          stage: 'form_opens',
          hint: this.stats.form_open_events != null
            ? `событий: ${this.stats.form_open_events}`
            : 'уник. сессии',
        },
        {
          label: 'Оставили заявку',
          value: this.stats.leads,
          cr: c.open_to_lead,
          stage: 'leads',
          hint: this.stats.lead_events != null
            ? `отправок: ${this.stats.lead_events}`
            : 'клиенты',
        },
        { label: 'Есть запись', value: this.stats.booked, cr: c.lead_to_booked, stage: 'booked', hint: 'клиенты' },
        { label: 'Пришли', value: this.stats.came, cr: c.booked_to_came, stage: 'came', hint: 'клиенты' },
        { label: 'Купили', value: this.stats.buyers, cr: c.came_to_buyer, stage: 'buyers', hint: 'клиенты' },
      ]
    },
    absoluteValues() {
      if (!this.stats) return []
      return [
        this.stats.visits,
        this.stats.form_opens,
        this.stats.leads,
        this.stats.booked,
        this.stats.came,
        this.stats.buyers,
      ]
    },
    chartSeries() {
      if (!this.stats) return []
      const abs = this.absoluteValues
      const pct = abs.map((v, i) => {
        if (i === 0) return 100
        const prev = abs[i - 1]
        return prev ? Math.round((v / prev) * 10000) / 100 : 0
      })
      return [{
        name: 'Конверсия к предыдущему',
        data: pct,
      }]
    },
    chartOptions() {
      const abs = this.absoluteValues
      return {
        chart: {
          toolbar: { show: false },
          events: {
            dataPointSelection: (_event, _ctx, config) => {
              const stages = ['visits', 'form_opens', 'leads', 'booked', 'came', 'buyers']
              const stage = stages[config.dataPointIndex]
              if (stage) this.openStage(stage)
            },
          },
        },
        plotOptions: {
          bar: { borderRadius: 4, columnWidth: '45%' },
        },
        dataLabels: {
          enabled: true,
          formatter: (val, opts) => {
            const n = abs[opts.dataPointIndex]
            return `${n}\n${val}%`
          },
        },
        yaxis: {
          max: 100,
          min: 0,
          title: { text: 'Конверсия шага, %' },
          labels: {
            formatter: (v) => `${v}%`,
          },
        },
        xaxis: {
          categories: [
            'Визиты',
            'Открыли форму',
            'Оставили заявку',
            'Есть запись',
            'Пришли',
            'Купили',
          ],
        },
        tooltip: {
          y: {
            formatter: (val, opts) => {
              const n = abs[opts.dataPointIndex]
              return `${n} (${val}%)`
            },
          },
        },
        colors: ['#5D87FF'],
      }
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    formatMoney(v) {
      return Number(v || 0).toLocaleString('ru-RU', {
        maximumFractionDigits: 0,
      })
    },
    openStage(stage) {
      this.$router.push({
        name: 'AnalyticsCustomers',
        query: stripEmpty({
          ...this.filters,
          stage,
        }),
      })
    },
    async load() {
      this.loading = true
      this.error = null
      try {
        this.stats = await Api.getAnalyticsFunnel(stripEmpty(this.filters))
      } catch (e) {
        this.error = 'Не удалось загрузить воронку'
        this.stats = null
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.funnel-stage {
  cursor: pointer;
}
.funnel-stage:hover {
  border-color: #5D87FF !important;
}
</style>
